import { memo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import {
  Edit2,
  Save,
  X,
  MessageSquare,
  Zap,
  DollarSign,
  Users,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { PlayerResourceLimitData } from '@/redux/types/resourceLimits'
import { ResourceDisplay } from '@/components/settings/ResourceLimits/ResourceDisplay'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface ResourceTableProps {
  playerLimits: PlayerResourceLimitData[]
  resourceLimitsEnabled: boolean
  editingId: number | null
  editValues: {
    message_limit: number | null
    token_limit: number | null
    currency_limit: number | null
  } | null
  isUpdating: boolean
  isGameCompleted?: boolean
  onEdit: (limit: PlayerResourceLimitData) => void
  onSave: (limit: PlayerResourceLimitData) => void
  onCancelEdit: () => void
  onInputChange: (
    field: 'message_limit' | 'token_limit' | 'currency_limit',
    value: string
  ) => void
}

interface ResourceTypeConfig {
  key: 'message_limit' | 'token_limit' | 'currency_limit'
  label: string
  type: 'messages' | 'tokens' | 'currency'
  usageKey: 'messages_used' | 'tokens_used' | 'currency_spent'
  remainingKey: 'messages_remaining' | 'tokens_remaining' | 'currency_remaining'
}

const RESOURCE_TYPES: (ResourceTypeConfig & {
  icon: typeof MessageSquare
  color: string
  bgColor: string
})[] = [
  {
    key: 'message_limit',
    label: 'Messages',
    type: 'messages',
    usageKey: 'messages_used',
    remainingKey: 'messages_remaining',
    icon: MessageSquare,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  {
    key: 'token_limit',
    label: 'Tokens',
    type: 'tokens',
    usageKey: 'tokens_used',
    remainingKey: 'tokens_remaining',
    icon: Zap,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  {
    key: 'currency_limit',
    label: 'Currency',
    type: 'currency',
    usageKey: 'currency_spent',
    remainingKey: 'currency_remaining',
    icon: DollarSign,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
  },
]

export const ResourceTable = memo(function ResourceTable({
  playerLimits,
  resourceLimitsEnabled,
  editingId,
  editValues,
  isUpdating,
  isGameCompleted = false,
  onEdit,
  onSave,
  onCancelEdit,
  onInputChange,
}: ResourceTableProps) {
  if (playerLimits.length === 0) {
    return (
      <Card className='p-12 border-2 border-dashed border-slate-200'>
        <div className='text-center space-y-3'>
          <div className='flex justify-center'>
            <div className='p-4 bg-slate-100 rounded-full'>
              <Users className='h-8 w-8 text-slate-400' />
            </div>
          </div>
          <div>
            <p className='text-base font-semibold text-slate-700 mb-1'>
              No Players Found
            </p>
            <p className='text-sm text-slate-500'>
              No players have resource limits configured yet. Players will
              receive default limits when they are invited to the room.
            </p>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <Card className='overflow-hidden border-2 shadow-lg'>
      <div className='overflow-x-auto'>
        <Table>
          <TableHeader className='bg-gradient-to-r from-slate-50 via-slate-100 to-slate-50'>
            <TableRow className='border-b-2 border-slate-200'>
              <TableHead className='font-semibold text-slate-700 sticky left-0 z-10 bg-gradient-to-r from-slate-50 via-slate-100 to-slate-50 min-w-[200px]'>
                <div className='flex items-center gap-2'>
                  <Users className='h-4 w-4' />
                  Player
                </div>
              </TableHead>
              {RESOURCE_TYPES.map((resource) => {
                const Icon = resource.icon
                return (
                  <TableHead
                    key={resource.key}
                    className={cn('font-semibold text-center min-w-[220px]')}
                  >
                    <div className='flex items-center justify-center gap-2'>
                      <Icon className={cn('h-4 w-4', resource.color)} />
                      <span className='text-slate-700'>{resource.label}</span>
                    </div>
                  </TableHead>
                )
              })}
              {resourceLimitsEnabled && (
                <TableHead className='font-semibold w-32 text-center text-slate-700'>
                  Actions
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {playerLimits.map((limit, index) => (
              <TableRow
                key={limit.id}
                className={cn(
                  'hover:bg-slate-50/50 transition-colors',
                  editingId === limit.id && 'bg-sky-50/50',
                  index % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'
                )}
              >
                <TableCell className='sticky left-0 z-10 font-medium'>
                  <div className='space-y-1.5'>
                    <div className='flex items-center gap-2 flex-wrap'>
                      <span className='font-semibold text-slate-800'>
                        {limit.player.name || limit.player.email}
                      </span>
                      {limit.player.team_name && (
                        <Badge
                          variant='outline'
                          className='text-xs bg-indigo-50 border-indigo-200 text-indigo-700'
                        >
                          {limit.player.team_name}
                        </Badge>
                      )}
                    </div>
                    <div className='text-xs text-slate-500 flex items-center gap-1'>
                      <span>{limit.player.email}</span>
                    </div>
                  </div>
                </TableCell>
                {RESOURCE_TYPES.map((resource) => {
                  const Icon = resource.icon
                  return (
                    <TableCell
                      key={resource.key}
                      className='min-w-[220px] text-center py-4'
                    >
                      {editingId === limit.id && editValues ? (
                        <div className='space-y-2 flex flex-col items-center'>
                          <div className='flex items-center gap-2'>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div>
                                    <Input
                                      type='number'
                                      min='0'
                                      value={editValues[resource.key] ?? ''}
                                      onChange={(e) =>
                                        onInputChange(
                                          resource.key,
                                          e.target.value
                                        )
                                      }
                                      placeholder='Unlimited'
                                      className={cn(
                                        'w-28 text-center font-medium',
                                        resource.key === 'token_limit' &&
                                          'border-purple-300 focus:border-purple-500',
                                        resource.key === 'message_limit' &&
                                          'border-green-300 focus:border-green-500',
                                        resource.key === 'currency_limit' &&
                                          'border-emerald-300 focus:border-emerald-500'
                                      )}
                                      disabled={isUpdating || isGameCompleted}
                                    />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent
                                  side='top'
                                  align='center'
                                  className='px-3 py-2 max-w-xs text-sm text-white rounded-md shadow-xl bg-slate-800 z-[100]'
                                >
                                  <div className='space-y-1'>
                                    <p>
                                      Enter a number to set a limit, leave empty
                                      to set unlimited, or set to 0 to not allow
                                      any.
                                    </p>
                                    {resource.key === 'token_limit' && (
                                      <p className='text-xs pt-1 border-t border-slate-600'>
                                        <strong>Recommended:</strong> Minimum
                                        500–800 tokens, Balanced 1,000–1,500
                                        tokens
                                      </p>
                                    )}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <span className='text-xs text-slate-500 font-medium'>
                              / {limit.usage[resource.usageKey]} used
                            </span>
                          </div>
                          <div
                            className={cn(
                              'flex items-center gap-1 text-xs px-2 py-1 rounded',
                              resource.bgColor
                            )}
                          >
                            <Icon className={cn('h-3 w-3', resource.color)} />
                            <span className={cn('font-medium', resource.color)}>
                              {resource.label}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className='flex justify-center'>
                          <ResourceDisplay
                            limit={limit[resource.key]}
                            used={limit.usage[resource.usageKey]}
                            remaining={limit.remaining[resource.remainingKey]}
                          />
                        </div>
                      )}
                    </TableCell>
                  )
                })}
                {resourceLimitsEnabled && (
                  <TableCell className='text-center py-4'>
                    {editingId === limit.id ? (
                      <div className='flex items-center justify-center gap-1.5'>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size='sm'
                                variant='ghost'
                                onClick={() => onSave(limit)}
                                disabled={isUpdating || isGameCompleted}
                                className='h-8 w-8 p-0 hover:bg-green-100 hover:text-green-700'
                              >
                                <Save className='h-4 w-4' />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Save changes</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size='sm'
                                variant='ghost'
                                onClick={onCancelEdit}
                                disabled={isUpdating || isGameCompleted}
                                className='h-8 w-8 p-0 hover:bg-red-100 hover:text-red-700'
                              >
                                <X className='h-4 w-4' />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Cancel editing</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    ) : (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size='sm'
                              variant='ghost'
                              onClick={() => onEdit(limit)}
                              disabled={isUpdating || isGameCompleted}
                              className='h-8 w-8 p-0 hover:bg-sky-100 hover:text-sky-700'
                            >
                              <Edit2 className='h-4 w-4' />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {isGameCompleted
                                ? 'Cannot edit when game is completed'
                                : 'Edit resource limits'}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  )
})
